import { useState, useEffect } from 'react'
import { subscriptionsAPI } from '../../services/api'

import { logger } from '@/utils/logger'

const statusTag: Record<string, { className: string; label: string }> = {
  ACTIVE: { className: 'wc-tag-accent', label: 'Aktiv' },
  TRIAL: { className: 'wc-tag-outline', label: 'Testversion' },
  CANCELLED: { className: 'wc-tag-neutral', label: 'Gekündigt' },
  CANCELLED_PENDING: { className: 'wc-tag-neutral', label: 'Kündigung beantragt' },
  EXPIRED: { className: 'wc-tag-neutral', label: 'Abgelaufen' },
}

export default function CustomerAppsPage() {
  const [subscriptions, setSubscriptions] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({})

  useEffect(() => {
    fetchSubscriptions()
  }, [])

  const fetchSubscriptions = async () => {
    try {
      const response = await subscriptionsAPI.getMy()
      setSubscriptions(response.data)
    } catch (err) {
      logger.error('Error loading subscriptions:', err)
      setError('Fehler beim Laden der Abonnements')
    } finally {
      setLoading(false)
    }
  }

  const handleCancellation = async (subscriptionId: number) => {
    if (!confirm('Möchten Sie dieses Abonnement wirklich kündigen? Es wird automatisch ein Support-Ticket erstellt.')) {
      return
    }

    setActionLoading((prev) => ({ ...prev, [`cancel_${subscriptionId}`]: true }))
    setError('')
    setSuccess('')

    try {
      await subscriptionsAPI.requestCancellation(subscriptionId)
      setSuccess('Kündigungsanfrage erfolgreich! Ein Support-Ticket wurde erstellt. Sie können den Status unter "Support" verfolgen.')
      await fetchSubscriptions()
    } catch (err) {
      logger.error('Error requesting cancellation:', err)
      setError('Fehler beim Senden der Kündigungsanfrage. Bitte versuchen Sie es später erneut.')
    } finally {
      setActionLoading((prev) => ({ ...prev, [`cancel_${subscriptionId}`]: false }))
    }
  }

  const handleToggleAutoRenew = async (subscriptionId: number, currentValue: boolean) => {
    setActionLoading((prev) => ({ ...prev, [`renew_${subscriptionId}`]: true }))
    setError('')
    setSuccess('')

    try {
      await subscriptionsAPI.toggleAutoRenew(subscriptionId)
      setSuccess(`Auto-Verlängerung wurde ${currentValue ? 'deaktiviert' : 'aktiviert'}.`)
      await fetchSubscriptions()
    } catch (err) {
      logger.error('Error toggling auto-renew:', err)
      setError('Fehler beim Ändern der Auto-Verlängerung. Bitte versuchen Sie es später erneut.')
    } finally {
      setActionLoading((prev) => ({ ...prev, [`renew_${subscriptionId}`]: false }))
    }
  }

  const getPrice = (subscription: any) => {
    if (subscription.app_plan) {
      const price = subscription.plan === 'YEARLY' ? subscription.app_plan.price_yearly : subscription.app_plan.price_monthly
      return `${price}€ / ${subscription.plan === 'YEARLY' ? 'Jahr' : 'Monat'}`
    }
    const price = subscription.plan === 'YEARLY' ? subscription.app?.price_yearly || '0.00' : subscription.app?.price_monthly || '0.00'
    return `${price}€ / ${subscription.plan === 'YEARLY' ? 'Jahr' : 'Monat'}`
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('de-DE')
  }

  const canBeCancelled = (status: string) => status === 'ACTIVE' || status === 'TRIAL'
  const canToggleAutoRenew = (status: string) => status === 'ACTIVE' || status === 'TRIAL'

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <div className="wc-spinner" />
      </div>
    )
  }

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 26, margin: '0 0 6px' }}>Meine Apps</h1>
        <p className="wc-muted" style={{ fontSize: 14, margin: 0 }}>Übersicht über alle Ihre App-Abonnements</p>
      </div>

      {error && <div className="wc-alert wc-alert-error" style={{ marginBottom: 24 }}>{error}</div>}
      {success && <div className="wc-alert wc-alert-success" style={{ marginBottom: 24 }}>{success}</div>}

      {subscriptions.length === 0 ? (
        <div className="wc-card wc-elev-sm" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <h3 className="wc-card-title" style={{ marginBottom: 8 }}>Keine Abonnements</h3>
          <p className="wc-muted" style={{ marginBottom: 20 }}>Sie haben derzeit keine App-Abonnements.</p>
          <a href="/apps" className="wc-btn wc-btn-primary">Apps entdecken</a>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 20 }}>
          {subscriptions.map((subscription) => {
            const tag = statusTag[subscription.status] || statusTag.ACTIVE
            return (
              <div key={subscription.id} className="wc-card wc-elev-sm" style={{ gap: 16 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    {subscription.app?.icon_url ? (
                      <img
                        src={subscription.app.icon_url}
                        alt={subscription.app.name}
                        style={{ width: 44, height: 44, borderRadius: 'var(--wc-radius-md)', objectFit: 'cover', flexShrink: 0 }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 'var(--wc-radius-md)',
                          background: 'var(--wc-accent-800)',
                          color: 'var(--wc-accent-100)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 18,
                          flexShrink: 0,
                        }}
                      >
                        {subscription.app?.name?.charAt(0) || 'A'}
                      </div>
                    )}
                    <div style={{ minWidth: 0 }}>
                      <h3 className="wc-card-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {subscription.app?.name || 'Unbekannte App'}
                      </h3>
                      <p className="wc-muted" style={{ fontSize: 12, margin: 0 }}>{subscription.app?.short_description || ''}</p>
                    </div>
                  </div>
                  <span className={`wc-tag ${tag.className}`} style={{ flexShrink: 0 }}>{tag.label}</span>
                </div>

                {subscription.status === 'CANCELLED_PENDING' && (
                  <div className="wc-alert wc-alert-info" style={{ margin: 0 }}>
                    <strong>Hinweis:</strong> Ihre Kündigungsanfrage wird derzeit von einem Administrator geprüft.
                  </div>
                )}

                <div>
                  {subscription.app_plan && (
                    <div className="wc-row">
                      <span className="wc-muted" style={{ fontSize: 13 }}>Plan</span>
                      <span style={{ fontSize: 13 }}>{subscription.app_plan.name}</span>
                    </div>
                  )}
                  <div className="wc-row">
                    <span className="wc-muted" style={{ fontSize: 13 }}>Abo-Typ</span>
                    <span className={`wc-tag ${subscription.plan === 'YEARLY' ? 'wc-tag-accent-2' : 'wc-tag-neutral'}`}>
                      {subscription.plan === 'YEARLY' ? 'Jährlich' : 'Monatlich'}
                    </span>
                  </div>
                  <div className="wc-row">
                    <span className="wc-muted" style={{ fontSize: 13 }}>Preis</span>
                    <span style={{ fontSize: 13 }}>{getPrice(subscription)}</span>
                  </div>
                  <div className="wc-row">
                    <span className="wc-muted" style={{ fontSize: 13 }}>Startdatum</span>
                    <span style={{ fontSize: 13 }}>{formatDate(subscription.start_date)}</span>
                  </div>
                  {subscription.end_date && (
                    <div className="wc-row">
                      <span className="wc-muted" style={{ fontSize: 13 }}>Enddatum</span>
                      <span style={{ fontSize: 13 }}>{formatDate(subscription.end_date)}</span>
                    </div>
                  )}
                  <div className="wc-row">
                    <span className="wc-muted" style={{ fontSize: 13 }}>Auto-Verlängerung</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13, color: subscription.auto_renew ? 'var(--wc-accent-300)' : undefined }}>
                        {subscription.auto_renew ? 'Aktiv' : 'Inaktiv'}
                      </span>
                      {canToggleAutoRenew(subscription.status) && (
                        <button
                          onClick={() => handleToggleAutoRenew(subscription.id, subscription.auto_renew)}
                          disabled={actionLoading[`renew_${subscription.id}`]}
                          className="wc-btn wc-btn-secondary"
                          style={{ padding: '3px 10px', fontSize: 12 }}
                        >
                          {actionLoading[`renew_${subscription.id}`] ? 'Lädt…' : 'Ändern'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {subscription.app?.features && (
                  <div>
                    <h4 style={{ fontSize: 13, margin: '0 0 8px' }}>Enthaltene Features</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {JSON.parse(subscription.app.features).map((feature: string, idx: number) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13 }} className="wc-muted">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--wc-accent)" strokeWidth="2" style={{ marginTop: 3, flexShrink: 0 }} aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {(subscription.app?.demo_url || subscription.app?.documentation_url) && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      {subscription.app?.demo_url && (
                        <a href={subscription.app.demo_url} target="_blank" rel="noopener noreferrer" className="wc-btn wc-btn-secondary" style={{ flex: 1, fontSize: 13 }}>
                          Demo öffnen
                        </a>
                      )}
                      {subscription.app?.documentation_url && (
                        <a href={subscription.app.documentation_url} target="_blank" rel="noopener noreferrer" className="wc-btn wc-btn-secondary" style={{ flex: 1, fontSize: 13 }}>
                          Dokumentation
                        </a>
                      )}
                    </div>
                  )}

                  {canBeCancelled(subscription.status) && (
                    <button
                      onClick={() => handleCancellation(subscription.id)}
                      disabled={actionLoading[`cancel_${subscription.id}`]}
                      className="wc-btn wc-btn-secondary wc-btn-block"
                      style={{ fontSize: 13, color: '#fca5a5', borderColor: 'color-mix(in srgb, #fca5a5 45%, transparent)' }}
                    >
                      {actionLoading[`cancel_${subscription.id}`] ? 'Wird gesendet…' : 'Abonnement kündigen'}
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
